<template>
    <div class="fax-preview">
        <v-layout row align-center class="mb-2">
            <div v-if="row.fileName" class="fax-file-name">
                <v-icon small class="mr-1">mdi-paperclip</v-icon>
                {{ row.fileName }}
            </div>
            <v-spacer></v-spacer>
            <v-btn
                v-if="file.fileURL"
                small
                outline
                color="success"
                class="mr-2"
                @click="fullScreen = true"
            >
                View PDF
            </v-btn>
            <v-tooltip
                v-if="showManageFiles && file.fileURL"
                bottom
                :disabled="hasLinkedAccount"
            >
                <span slot="activator" class="manage-files-action mr-2">
                    <v-btn
                        small
                        color="success"
                        :disabled="!hasLinkedAccount"
                        @click="openManageFiles"
                    >
                        Manage files
                    </v-btn>
                </span>
                <span>Link this communication to an account before managing files.</span>
            </v-tooltip>
            <v-btn
                v-if="!hideAssign && !row.accountID && openAssignFax"
                small
                color="success"
                dark
                @click="openAssignFax(row)"
            >
                Assign Account
            </v-btn>
        </v-layout>

        <div v-if="faxText" class="fax-plain-text mb-3">
            <span>{{ textExpanded ? faxTextFirstLine : faxTextPreview }}</span><button
                v-if="faxTextHasMore"
                type="button"
                class="fax-plain-text-toggle"
                :title="textExpanded ? 'Hide text' : 'Show full text'"
                @click="textExpanded = !textExpanded"
            >
                <v-icon small color="#78909c">
                    {{ textExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
                </v-icon>
            </button><template v-if="textExpanded && faxTextRest">{{ "\n" + faxTextRest }}</template>
        </div>

        <div v-if="loading" class="fax-preview-loading">
            <v-progress-circular indeterminate color="success" size="28"></v-progress-circular>
            <div class="mt-2">Loading fax...</div>
        </div>
        <img
            v-else-if="kind === 'image'"
            class="fax-preview-image"
            :src="file.fileURL"
            :alt="file.fileName"
        />
        <div v-else-if="kind === 'pdf' || kind === 'other'" class="fax-preview-frame-wrap">
            <iframe
                class="fax-preview-frame"
                :src="previewUrl"
            ></iframe>
        </div>
        <div v-else-if="!row.hasAttachment" class="grey--text">
            No attachment
        </div>
        <document-preview-dialog
            v-model="fullScreen"
            :url="file.fileURL"
            :name="file.fileName || row.fileName"
            :kind="kind"
            :account-name="row && row.accountName"
            :accountID="row && row.accountID"
            :subject="row && row.subject"
            :received="row && row.date"
        ></document-preview-dialog>
    </div>
</template>
<script>
export default {
    inject: {
        openAssignFax: {
            default: null,
        },
        openManageFilesDialog: {
            default: null,
        },
    },
    props: {
        row: Object,
        columns: Array,
        reloadGrid: Function,
        hideAssign: {
            type: Boolean,
            default: false,
        },
        showManageFiles: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            loading: false,
            file: {},
            fullScreen: false,
            textExpanded: false,
        };
    },
    watch: {
        rowId: function() {
            this.textExpanded = false;
        },
    },
    computed: {
        rowId() {
            return this.row && this.row.id;
        },
        faxText() {
            return this.trimText(this.row && this.row.text);
        },
        faxTextFirstLine() {
            if (!this.faxText) {
                return "";
            }
            return this.faxText.split(/\r?\n/)[0].trim();
        },
        faxTextHasMore() {
            return !!(this.faxText && this.faxText !== this.faxTextFirstLine);
        },
        faxTextRest() {
            if (!this.faxText) {
                return "";
            }
            var idx = this.faxText.search(/\r?\n/);
            if (idx < 0) {
                return "";
            }
            return this.faxText.slice(idx).replace(/^\r?\n/, "");
        },
        faxTextPreview() {
            if (!this.faxTextHasMore) {
                return this.faxText;
            }
            return this.faxTextFirstLine + "..";
        },
        kind() {
            return this.fileKind(this.file);
        },
        hasLinkedAccount() {
            if (!this.row || this.row.needsAccount) {
                return false;
            }
            var id = (this.row.accountID || "").trim();
            return !!id;
        },
        previewUrl() {
            if (!this.file || !this.file.fileURL) {
                return "";
            }

            if (this.kind === "pdf" && this.file.fileURL.indexOf("#") === -1) {
                return this.file.fileURL + "#toolbar=0&navpanes=0&view=FitH";
            }

            return this.file.fileURL;
        },
    },
    async created() {
        await this.loadFile();
    },
    methods: {
        trimText(text) {
            if (!text) return text;
            // white-space: pre-line turns leading/trailing newlines into
            // blank lines, so trim the ends (internal line breaks stay intact).
            return text.trimStart().trimEnd();
        },
        async loadFile() {
            if (!this.row || !this.row.id || !this.row.hasAttachment) {
                return;
            }

            this.loading = true;

            try {
                const file = await this.$http.get(
                    "api/faxes/getFile?faxID=" + this.row.id,
                );
                this.file = file || {};
            } catch (e) {
                this.file = {};
            }

            this.loading = false;
        },
        openManageFiles() {
            if (!this.hasLinkedAccount || !this.row || !this.row.id) {
                return;
            }

            if (this.openManageFilesDialog) {
                this.openManageFilesDialog({
                    source: "fax",
                    sourceId: this.row.id,
                    communicationID: this.row.communicationID,
                });
                return;
            }

            var route = this.$router.resolve({
                name: "DocumentSplitter",
                params: {
                    source: "fax",
                    sourceId: String(this.row.id),
                },
            });
            window.open(route.href, "_blank");
        },
        fileKind(file) {
            const name = ((file && file.fileName) || "").toLowerCase();
            const type = ((file && file.contentType) || "").toLowerCase();
            const ext = name.includes(".") ? name.split(".").pop() : "";

            if (
                type.startsWith("image/") ||
                ["jpg", "jpeg", "png", "gif", "webp", "bmp", "tif", "tiff"].includes(ext)
            ) {
                return "image";
            }
            if (type === "application/pdf" || ext === "pdf") {
                return "pdf";
            }
            if (file && file.fileURL) {
                return "other";
            }

            return "";
        },
    },
};
</script>
<style>
.fax-preview {
    max-width: 1000px;
    padding: 0 4px 12px 16px;
}

.fax-file-name {
    color: #607d8b;
    font-size: 13px;
    font-weight: 500;
    display: flex;
    align-items: center;
}

.manage-files-action {
    display: inline-block;
}

.fax-plain-text {
    white-space: pre-line;
    line-height: 1.4;
    font-size: 11px;
    color: #90a4ae;
}

.fax-plain-text-toggle {
    display: inline-block;
    border: 0;
    background: transparent;
    padding: 0;
    margin: 0 0 0 2px;
    vertical-align: middle;
    cursor: pointer;
    line-height: 1;
}

.fax-preview-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;
    color: #90a4ae;
    font-size: 13px;
}

.fax-preview-image {
    display: block;
    max-width: 100%;
    max-height: 80vh;
    margin: 0 auto;
    border-radius: 6px;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.12);
}

.fax-preview-frame-wrap {
    background: #eceff1;
    border-radius: 8px;
    padding: 12px;
    box-shadow: inset 0 0 0 1px #e0e0e0;
}

.fax-preview-frame {
    display: block;
    width: 100%;
    height: 78vh;
    border: 0;
    border-radius: 4px;
    background: #fff;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.15);
}
</style>
